import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Save, Plus, Trash2 } from "lucide-react";

export default function SiteSettings() {
  const { activeTenant } = useTenant();
  const [site, setSite] = useState(null);
  const [savedTs, setSavedTs] = useState(null);
  const [tab, setTab] = useState("nav");

  useEffect(() => {
    if (!activeTenant) return;
    api.get("/site").then(({ data }) => setSite(data));
  }, [activeTenant?.id]);  // eslint-disable-line

  const save = async () => {
    await api.patch("/site", { nav: site.nav, footer: site.footer, settings: site.settings });
    setSavedTs(new Date().toLocaleTimeString());
  };

  if (!site) return <div className="p-10 text-[var(--n-text-muted)]">Loading…</div>;

  const s = site.settings || {};
  const updateSettings = (patch) => setSite({ ...site, settings: { ...site.settings, ...patch } });

  return (
    <div className="p-8 max-w-5xl" data-testid="site-settings-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Website · {activeTenant?.name}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Site settings</h1>
        </div>
        <button onClick={save} className="n-btn-blue n-btn flex items-center gap-2" data-testid="site-save">
          <Save size={14} /> {savedTs ? `SAVED · ${savedTs}` : "SAVE"}
        </button>
      </div>

      <div className="flex border-b border-[var(--n-border)] mb-6">
        {["nav", "footer", "contact", "fonts", "social"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-mono-plex uppercase tracking-wider border-b-2 -mb-px ${tab === t ? "border-white text-white" : "border-transparent text-[var(--n-text-sec)]"}`}
            data-testid={`site-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "nav" && (
        <SimpleList
          title="Navigation menu"
          items={site.nav || []}
          onChange={(nav) => setSite({ ...site, nav })}
          fields={[{ key: "label", label: "Label" }, { key: "href", label: "Path" }]}
          testid="nav-editor"
        />
      )}

      {tab === "footer" && (
        <div className="space-y-4">
          <div className="n-card p-5">
            <label className="n-label block mb-1">Copyright text</label>
            <input className="n-input" value={site.footer?.copyright || ""} onChange={(e) => setSite({ ...site, footer: { ...site.footer, copyright: e.target.value } })} data-testid="footer-copyright" />
          </div>
          <FooterColumnsEditor
            columns={site.footer?.columns || []}
            onChange={(cols) => setSite({ ...site, footer: { ...site.footer, columns: cols } })}
          />
        </div>
      )}

      {tab === "contact" && (
        <div className="space-y-4">
          <SimpleList
            title="Phone numbers"
            items={(s.phones || []).map((p) => ({ value: p }))}
            onChange={(arr) => updateSettings({ phones: arr.map((a) => a.value).filter(Boolean) })}
            fields={[{ key: "value", label: "Phone" }]}
            testid="phones-editor"
          />
          <SimpleList
            title="Email addresses"
            items={(s.emails || []).map((e) => ({ value: e }))}
            onChange={(arr) => updateSettings({ emails: arr.map((a) => a.value).filter(Boolean) })}
            fields={[{ key: "value", label: "Email" }]}
            testid="emails-editor"
          />
          <SimpleList
            title="Business hours"
            items={s.business_hours || []}
            onChange={(hours) => updateSettings({ business_hours: hours })}
            fields={[{ key: "day", label: "Day" }, { key: "hours", label: "Hours" }]}
            testid="hours-editor"
          />
        </div>
      )}

      {tab === "fonts" && (
        <div className="n-card p-5 grid grid-cols-2 gap-3">
          <div>
            <label className="n-label block mb-1">Heading font</label>
            <input className="n-input" value={s.font_heading || ""} onChange={(e) => updateSettings({ font_heading: e.target.value })} data-testid="font-heading" />
          </div>
          <div>
            <label className="n-label block mb-1">Body font</label>
            <input className="n-input" value={s.font_body || ""} onChange={(e) => updateSettings({ font_body: e.target.value })} data-testid="font-body" />
          </div>
          <div className="col-span-2">
            <label className="n-label block mb-1">Favicon URL</label>
            <input className="n-input" value={s.favicon_url || ""} onChange={(e) => updateSettings({ favicon_url: e.target.value })} data-testid="favicon-url" />
          </div>
        </div>
      )}

      {tab === "social" && (
        <div className="n-card p-5 grid grid-cols-2 gap-3">
          {["facebook", "instagram", "twitter", "linkedin", "youtube", "tiktok"].map((k) => (
            <div key={k}>
              <label className="n-label block mb-1">{k}</label>
              <input className="n-input" value={s.social?.[k] || ""} onChange={(e) => updateSettings({ social: { ...s.social, [k]: e.target.value } })} data-testid={`social-${k}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimpleList({ title, items, onChange, fields, testid }) {
  const add = () => {
    const empty = Object.fromEntries(fields.map((f) => [f.key, ""]));
    onChange([...items, empty]);
  };
  return (
    <div className="n-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-cab font-bold text-lg">{title}</div>
        <button onClick={add} className="n-btn-ghost n-btn text-xs flex items-center gap-1"><Plus size={11}/> ADD</button>
      </div>
      {items.map((it, idx) => (
        <div key={idx} className="border border-[var(--n-border)] p-3 mb-2 relative">
          <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="absolute top-1 right-1 text-[var(--n-danger)]"><Trash2 size={11}/></button>
          <div className="grid grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] font-mono-plex uppercase text-[var(--n-text-muted)] block mb-1">{f.label}</label>
                <input className="n-input text-xs" value={it[f.key] || ""} onChange={(e) => onChange(items.map((x, i) => i === idx ? { ...x, [f.key]: e.target.value } : x))} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="text-xs font-mono-plex text-[var(--n-text-muted)] text-center py-4">Empty. Click ADD.</div>}
    </div>
  );
}

function FooterColumnsEditor({ columns, onChange }) {
  const addCol = () => onChange([...columns, { heading: "", links: [] }]);
  return (
    <div className="n-card p-5" data-testid="footer-columns-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="font-cab font-bold text-lg">Footer columns</div>
        <button onClick={addCol} className="n-btn-ghost n-btn text-xs flex items-center gap-1"><Plus size={11}/> ADD COLUMN</button>
      </div>
      {columns.map((col, idx) => (
        <div key={idx} className="border border-[var(--n-border)] p-3 mb-3 relative">
          <button onClick={() => onChange(columns.filter((_, i) => i !== idx))} className="absolute top-1 right-1 text-[var(--n-danger)]"><Trash2 size={11}/></button>
          <label className="text-[10px] font-mono-plex uppercase text-[var(--n-text-muted)] block mb-1">Heading</label>
          <input
            className="n-input text-xs mb-3"
            value={col.heading || ""}
            onChange={(e) => onChange(columns.map((x, i) => i === idx ? { ...x, heading: e.target.value } : x))}
          />
          <div className="pl-3 border-l border-[var(--n-border)]">
            <SimpleList
              title="Links"
              items={col.links || []}
              onChange={(links) => onChange(columns.map((x, i) => i === idx ? { ...x, links } : x))}
              fields={[{ key: "label", label: "Label" }, { key: "href", label: "Path" }]}
              testid={`footer-col-${idx}-links`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
